<template>
  <div
    class="bui-tbl-col"
    :class="{ first: isFirst, last: isLast }"
    :style="widthStyle"
  >
    <slot name="header" />
    <div class="rows" role="presentation">
      <slot name="rows" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    width?: string | number;
    minWidth?: string | number;
    grow?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
  }>(),
  { grow: false, isFirst: false, isLast: false },
);

function toCssLen(v?: string | number): string | undefined {
  if (v == null) return undefined;
  return typeof v === 'number' ? `${v}px` : v;
}

const widthStyle = computed(() => {
  const w = toCssLen(props.width);
  const mw = toCssLen(props.minWidth);
  const style: Record<string, string> = {};
  if (props.grow) {
    /*
     * flex-basis must be the column's minimum, NOT `auto`: cell text is
     * nowrap, so a column's min-content equals its longest line, and with
     * basis:auto the row's min-content (its scroll width) balloons to the
     * text length instead of the configured minimums. With the minimum as
     * basis the row only scrolls when the minimums genuinely can't fit,
     * and free space is shared in proportion to each column's minimum.
     */
    const growFactor = typeof props.minWidth === 'number' ? props.minWidth : 100;
    style.flex = `${growFactor} 1 ${mw ?? '0%'}`;
    if (mw) style.minWidth = mw;
  } else if (w) {
    style.width = w;
    style.flexShrink = '0';
  }
  return style;
});
</script>

<style lang="scss" scoped>
.bui-tbl-col {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  min-width: 0;
  overflow: hidden;
  background: var(--bui-tbl-line, #ffd9e6);

  .rows {
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
    /* Row dividers use border-bottom on TblRowCell (avoids flex gap subpixel bands). */
    gap: 0;
    background: var(--bui-tbl-line, #ffd9e6);
  }
}
</style>
